<template>
  <div
    v-for="(numberOfFamilyPlan, index) in numberOfFamilyPlans"
    :key="numberOfFamilyPlan"
    class="flex flex-col gap-8"
  >
    <div class="flex justify-between">
      <h4 class="heading-xxs">Familjeanslutning {{ numberOfFamilyPlan }}</h4>
      <button
        v-if="index > 0"
        :id="`remove_family_plan_button_${index}`"
        :aria-label="removePlanAriaLabel(numberOfFamilyPlan)"
        :aria-disabled="!canRemovePlan(numberOfFamilyPlan)"
        class="underline body-link-md active:bg-surface-action-hover"
        :class="{
          'text-text-link': canRemovePlan(numberOfFamilyPlan),
          'hover:text-text-link-hover': canRemovePlan(numberOfFamilyPlan),
          'text-text-disabled': !canRemovePlan(numberOfFamilyPlan),
        }"
        @click="canRemovePlan(numberOfFamilyPlan) ? removeFamilyPlan() : ''"
        @keyup.enter.once.self.prevent="
          canRemovePlan(numberOfFamilyPlan) ? removeFamilyPlan() : ''
        "
        @keyup.space.once.self.prevent="
          canRemovePlan(numberOfFamilyPlan) ? removeFamilyPlan() : ''
        "
      >
        Ta bort
      </button>
    </div>

    <button
      :id="`family${index}`"
      :class="{
        'flex flex-col justify-center p-16 rounded-md align-center border-border-primary border-md bg-surface-action-1 mb-16 shadow-button':
          shouldShowPlanDetails(numberOfFamilyPlan),
        'flex justify-center p-16 rounded-md align-center cursor-pointer border-border-primary dashed-border-clip border-md border-dashed mb-16 hover:bg-surface-action-hover active:bg-border-secondary':
          !shouldShowPlanDetails(numberOfFamilyPlan),
        'pointer-events-none': index === 0,
      }"
      :aria-disabled="index === 0"
      :aria-label="
        shouldShowPlanDetails(numberOfFamilyPlan)
          ? removePlanAriaLabel(numberOfFamilyPlan)
          : `Lägg till Familjeanslutnings nummer ${numberOfFamilyPlan} till huvudabonnemanget`
      "
      @click="familyPlanButtonClickHandler(numberOfFamilyPlan)"
      @keyup.enter.once.self.prevent="
        familyPlanButtonClickHandler(numberOfFamilyPlan)
      "
      @keyup.space.once.self.prevent="
        familyPlanButtonClickHandler(numberOfFamilyPlan)
      "
    >
      <FamilyAddFamilyButtonContent
        v-if="!shouldShowPlanDetails(numberOfFamilyPlan)"
      />
      <FamilyAddedFamilyButtonContent v-else />
    </button>
  </div>
</template>

<script lang="ts" setup>
const familyStore = useFamilyStore();

interface Props {
  numberOfFamilyPlans: number;
}

const props = defineProps<Props>();

const removePlanAriaLabel = (numberOfPlan: number) =>
  numberOfPlan > 1
    ? `Ta bort Familjeanslutnings nummer ${numberOfPlan} från huvudabonnemanget`
    : '';

const familyPlanButtonClickHandler = (numberOfPlan: number) => {
  if (shouldShowPlanDetails(numberOfPlan) && canRemovePlan(numberOfPlan)) {
    removeFamilyPlan();
    return;
  }

  if (!shouldShowPlanDetails(numberOfPlan)) {
    addFamilyPlan();
    return;
  }

  familyStore.ui.generalErrorMessage =
    'Det går inte att exkludera det här familjeabonnemanget från en beställning.';
};

const canRemovePlan = (planNumber: number) =>
  planNumber > 1 && familyStore.numberOfFamilyPlanConnections >= planNumber;

const shouldShowPlanDetails = (planNumber: number) =>
  familyStore.numberOfFamilyPlanConnections >= planNumber;

const addFamilyPlan = () => {
  if (familyStore.numberOfFamilyPlanConnections >= props.numberOfFamilyPlans) {
    return;
  }

  familyStore.numberOfFamilyPlanConnections += 1;
};

const removeFamilyPlan = () => {
  if (familyStore.numberOfFamilyPlanConnections <= 1) {
    return;
  }

  familyStore.numberOfFamilyPlanConnections -= 1;
};
</script>

<style scoped lang="scss"></style>
